/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { VApplication } from "../../application";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { getAgent } from "./agent";
import {
  CreateAttributeProps,
  CreateFeatureProps,
  CreateSectionOptionProps,
  CreateSectionProps,
  CreateValueProps,
  DeleteAttributeProps,
  DeleteFeatureProps,
  DeleteSectionOptionProps,
  DeleteSectionProps,
  DeleteValueProps,
  ImportGlobalAssetProps,
  ImportGlobalAssetsProps,
  ImportSectionAssetProps,
  ImportSectionAssetsProps,
  UpdateAttributeProps,
  UpdateFeatureProps,
  UpdateSectionOptionProps,
  UpdateValueProps,
} from "./components";
const is = require("is_js");

const SERVICE_NAME = "SectionOptionTypeService";
const log: any = LoggerModule.getInstance();

export class PresentationAccordianPageService {
  private static instance: PresentationAccordianPageService;
  private vapp: VApplication;

  private constructor(props: any = {}) {
    this.vapp = VApplication.getInstance();
  }

  public static getInstance(props: any = {}): PresentationAccordianPageService {
    if (!PresentationAccordianPageService.instance) {
      PresentationAccordianPageService.instance =
        new PresentationAccordianPageService(props);
    }
    return PresentationAccordianPageService.instance;
  }

  public getStaticPageContent() {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();

    compProps = agent.getStaticPageContentSync({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    return content;
  }

  public async getPageContent(args?: any) {
    let content: any = {};
    let compProps: any = null;
    const agent = getAgent();

    // Header Props
    compProps = await agent.getHeaderProps({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    const presentationId =
      args?.presentationId ?? args?.id ?? args?.location?.state?.presentationId;

    if (presentationId) {
      compProps = await agent.getTemplateFeatures({
        presentationId,
      });

      if (compProps) {
        content = {
          ...content,
          templateFeatures: compProps,
          globalAssets: compProps?.globalAssets ?? [],
          sectionAssets: compProps?.sectionAssets ?? [],
        };
      }
    }
    // Feature Section Props
    compProps = await agent.getSectionOptionProps(args);
    if (compProps) {
      content = { ...content, ...compProps };
    }

    // Footer Props
    compProps = await agent.getFooterProps({});
    if (compProps) {
      content = { ...content, ...compProps };
    }
    console.log("content==", content);
    return content;
  }

  public async createSection(args: CreateSectionProps) {
    const agent = getAgent();
    return agent.createSection(args);
  }

  public async updateSection(args: UpdateSectionOptionProps) {
    const agent = getAgent();
    return agent.updateSection(args);
  }

  public async deleteSection(args: DeleteSectionProps) {
    const agent = getAgent();
    return agent.deleteSection(args);
  }
  public async createSectionOption(args: CreateSectionOptionProps) {
    const agent = getAgent();
    return agent.createSectionOption(args);
  }

  public async updateSectionOption(args: UpdateSectionOptionProps) {
    const agent = getAgent();
    return agent.updateSectionOption(args);
  }

  public async deleteSectionOption(args: DeleteSectionOptionProps) {
    const agent = getAgent();
    return agent.deleteSectionOption(args);
  }

  public async createFeature(args: CreateFeatureProps) {
    const agent = getAgent();
    return agent.createFeature(args);
  }

  public async updateFeature(args: UpdateFeatureProps) {
    const agent = getAgent();
    return agent.updateFeature(args);
  }

  public async deleteFeature(args: DeleteFeatureProps) {
    const agent = getAgent();
    return agent.deleteFeature(args);
  }

  public async createAttribute(args: CreateAttributeProps) {
    const agent = getAgent();
    return agent.createAttribute(args);
  }

  public async updateAttribute(args: UpdateAttributeProps) {
    const agent = getAgent();
    return agent.updateAttribute(args);
  }

  public async deleteAttribute(args: DeleteAttributeProps) {
    const agent = getAgent();
    return agent.deleteAttribute(args);
  }

  public async createValue(args: CreateValueProps) {
    const agent = getAgent();
    return agent.createValue(args);
  }

  public async updateValue(args: UpdateValueProps) {
    const agent = getAgent();
    return agent.updateValue(args);
  }

  public async deleteValue(args: DeleteValueProps) {
    const agent = getAgent();
    return agent.deleteValue(args);
  }

  public async getCostumeTypePreview(costumeTypeId: string) {
    const agent = getAgent();
    return agent.getCostumeTypePreview(costumeTypeId);
  }

  public async getTemplateCostumeTypes() {
    const agent = getAgent();
    return agent.getTemplateCostumeTypes();
  }

  public async importSectionOptType(args: {
    presentation: string;
    templateCostumeType: string;
  }) {
    const agent = getAgent();
    return agent.importSectionOptType(args);
  }

  public async importCostumeType(args: {
    presentation: string;
    templateCostumeType: string;
  }) {
    const agent = getAgent();
    return agent.importCostumeType(args);
  }
  public async importSectionAsset(args: ImportSectionAssetProps) {
    const agent = getAgent();
    return agent.importSectionAsset(args);
  }

  public async importSectionAssets(args: ImportSectionAssetsProps) {
    const agent = getAgent();
    return agent.importSectionAssets(args);
  }
  public async importGlobalAsset(args: ImportGlobalAssetProps) {
    const agent = getAgent();
    return agent.importGlobalAsset(args);
  }

  public async importGlobalAssets(args: ImportGlobalAssetsProps) {
    const agent = getAgent();
    return agent.importGlobalAssets(args);
  }
}
